import React from 'react';
import { Plus } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { PricingPlanCard } from './PricingPlanCard';

export interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: {
    amount: number;
    currency: string;
    period: string;
    originalAmount?: number;
  };
  features: string[];
  highlighted: boolean;
  highlightLabel?: string;
  ctaButton: {
    text: string;
    url: string;
    style: string;
    target: string;
  };
  badge?: {
    text: string;
    color: string;
    textColor: string;
  };
  order: number;
}

interface PricingData {
  title?: string;
  subtitle?: string;
  description?: string;
  columns?: string;
  alignment?: string;
  cardStyle?: string;
  hoverEffect?: string;
  showDescription?: boolean;
  showOriginalPrice?: boolean;
  showBadges?: boolean;
  cardShadow?: boolean;
  cardBorder?: boolean;
  plans?: PricingPlan[];
  customColors?: {
    background?: string;
    text?: string;
    cardBackground?: string;
    cardText?: string;
    priceColor?: string;
    featureColor?: string;
  };
}

interface PricingEditorProps {
  formData: PricingData;
  setFormData: (data: PricingData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const PricingEditor: React.FC<PricingEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData || {};
  const plans = data.plans || [];
  const themeColors = useCustomColors();

  const addPlan = () => {
    const newPlan: PricingPlan = {
      id: `plan-${Date.now()}`,
      name: 'New Plan',
      description: '',
      price: {
        amount: 0,
        currency: 'USD',
        period: 'month',
        originalAmount: undefined,
      },
      features: ['Feature 1'],
      highlighted: false,
      highlightLabel: '',
      ctaButton: {
        text: 'Get Started',
        url: '/signup',
        style: 'primary',
        target: '_self',
      },
      badge: undefined,
      order: plans.length,
    };
    setFormData({ ...data, plans: [...plans, newPlan] });
  };

  const updatePlan = (index: number, updates: Partial<PricingPlan>) => {
    const updatedPlans = [...plans];
    updatedPlans[index] = { ...updatedPlans[index], ...updates };
    setFormData({ ...data, plans: updatedPlans });
  };

  const removePlan = (index: number) => {
    const updatedPlans = [...plans];
    updatedPlans.splice(index, 1);
    setFormData({ ...data, plans: updatedPlans });
  };

  const movePlan = (index: number, direction: 'up' | 'down') => {
    const updatedPlans = [...plans];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < updatedPlans.length) {
      [updatedPlans[index], updatedPlans[newIndex]] = [updatedPlans[newIndex], updatedPlans[index]];
      setFormData({ ...data, plans: updatedPlans });
    }
  };

  const setHighlightedPlan = (index: number) => {
    const updatedPlans = [...plans];
    updatedPlans.forEach((plan, i) => {
      plan.highlighted = i === index;
    });
    setFormData({ ...data, plans: updatedPlans });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('pricing', themeColors);

  return (
    <div className="space-y-6">
      {/* Header Content */}
      <HeaderSection data={data} setFormData={setFormData} />

      {/* Layout & Style Options */}
      <LayoutStyleSection data={data} setFormData={setFormData} />

      {/* Pricing Plans */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Pricing Plans</h3>
          <button
            type="button"
            onClick={addPlan}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <Plus size={16} className="mr-2" />
            Add Plan
          </button>
        </div>

        <div className="space-y-6">
          {plans.map((plan, index) => (
            <PricingPlanCard
              key={plan.id || index}
              plan={plan}
              index={index}
              totalPlans={plans.length}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
              onUpdate={updates => updatePlan(index, updates)}
              onRemove={() => removePlan(index)}
              onMove={direction => movePlan(index, direction)}
              onSetHighlighted={() => setHighlightedPlan(plan.highlighted ? -1 : index)}
            />
          ))}

          {plans.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500 mb-4">No pricing plans added yet</p>
              <button
                type="button"
                onClick={addPlan}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus size={16} className="mr-2" />
                Add First Plan
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize pricing section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface HeaderSectionProps {
  data: PricingData;
  setFormData: (data: PricingData) => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ data, setFormData }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <h3 className="text-lg font-medium text-gray-900 mb-4">Header Content</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Choose Your Perfect Plan"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Flexible pricing options designed to grow with your business"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={data.description || ''}
          onChange={e => setFormData({ ...data, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Start with our free plan and upgrade as your needs evolve..."
        />
      </div>
    </div>
  </div>
);

interface LayoutStyleSectionProps {
  data: PricingData;
  setFormData: (data: PricingData) => void;
}

const LayoutStyleSection: React.FC<LayoutStyleSectionProps> = ({ data, setFormData }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <h3 className="text-lg font-medium text-gray-900 mb-4">Layout & Style</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
        <select
          value={data.columns || '3'}
          onChange={e => setFormData({ ...data, columns: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="1">1 Column</option>
          <option value="2">2 Columns</option>
          <option value="3">3 Columns</option>
          <option value="4">4 Columns</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
        <select
          value={data.alignment || 'center'}
          onChange={e => setFormData({ ...data, alignment: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Card Style</label>
        <select
          value={data.cardStyle || 'default'}
          onChange={e => setFormData({ ...data, cardStyle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="default">Default</option>
          <option value="minimal">Minimal</option>
          <option value="bordered">Bordered</option>
          <option value="rounded">Rounded</option>
          <option value="modern">Modern</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Hover Effect</label>
        <select
          value={data.hoverEffect || 'lift'}
          onChange={e => setFormData({ ...data, hoverEffect: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">None</option>
          <option value="lift">Lift</option>
          <option value="zoom">Zoom</option>
          <option value="glow">Glow</option>
        </select>
      </div>
    </div>

    <div className="mt-4 space-y-3">
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={data.showDescription !== false}
          onChange={e => setFormData({ ...data, showDescription: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="ml-2 text-sm text-gray-700">Show Plan Descriptions</span>
      </label>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={data.showOriginalPrice !== false}
          onChange={e => setFormData({ ...data, showOriginalPrice: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="ml-2 text-sm text-gray-700">Show Original Prices (for discounts)</span>
      </label>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={data.showBadges !== false}
          onChange={e => setFormData({ ...data, showBadges: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="ml-2 text-sm text-gray-700">Show Badges & Labels</span>
      </label>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={data.cardShadow !== false}
          onChange={e => setFormData({ ...data, cardShadow: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="ml-2 text-sm text-gray-700">Card Shadow</span>
      </label>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={data.cardBorder === true}
          onChange={e => setFormData({ ...data, cardBorder: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="ml-2 text-sm text-gray-700">Card Border</span>
      </label>
    </div>
  </div>
);
